import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSButton from "views/system-design/DSButton";
import DSTable from "views/system-design/Table/DSTable";
import DSTableHead from "views/system-design/Table/DSTableHead";
import DSTableBody from "views/system-design/Table/DSTableBody";
import DSTableRow from "views/system-design/Table/DSTableRow";
import DSTableCell from "views/system-design/Table/DSTableCell";
import DSTableContainer from "views/system-design/Table/DSTableContainer";
import DSDialog from "views/system-design/DSDialog";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSTooltip from "views/system-design/DSTooltip";
import DSChip from "views/system-design/DSChip";
import PageHeader from "components/PageHeader";
import ConfirmationModal from "views/ConfirmationModal";
import { extractApiErrorMessage } from "../../../utils/util";
import { ContentProfileSummary } from "../../../api/admin";
import {
  CONTENT_PROFILES_QUERY_KEY,
  useContentProfiles,
  useDeleteContentProfile,
} from "../hooks/useContentProfiles";
import ContentProfileModal from "./ContentProfileModal";
import { isReady } from "../utils/profileStatus";

const ContentProfiles = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: profiles,
    isLoading,
    isFetching,
    isError,
    error,
  } = useContentProfiles();

  const [editing, setEditing] = useState<ContentProfileSummary | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleting, setDeleting] = useState<ContentProfileSummary | null>(null);
  // Outcome of the last delete, success or failure.
  const [notice, setNotice] = useState<string | null>(null);

  const { mutate: remove } = useDeleteContentProfile();

  const openCreate = () => {
    setEditing(null);
    setIsModalOpen(true);
  };

  const openRename = (profile: ContentProfileSummary) => {
    setEditing(profile);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!deleting) return;
    const profile = deleting;
    setDeleting(null);
    remove(profile.id, {
      onError: (e) =>
        setNotice(extractApiErrorMessage(e, "Failed to delete the profile")),
    });
  };

  return (
    <DSBox>
      <PageHeader
        overline="Devices"
        title="Content Profiles"
        subtitle="Prepare a bundle of settings and collections once, then push it to every assigned device with Sync"
      />

      {isLoading ? (
        <DSBox sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <DSCircularProgress />
        </DSBox>
      ) : isError ? (
        <DSBox sx={{ p: 2 }}>
          <DSTypography color="error">
            Failed to load profiles:{" "}
            {error instanceof Error ? error.message : "Unknown error"}
          </DSTypography>
        </DSBox>
      ) : (
        <DSBox>
          <DSBox
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
              alignItems: "center",
              gap: 1,
            }}
          >
            {isFetching && <DSCircularProgress size={18} />}
            <DSButton variant="contained" size="small" onClick={openCreate}>
              Create profile
            </DSButton>
            <DSButton
              variant="outlined"
              size="small"
              disabled={isFetching}
              onClick={() =>
                queryClient.invalidateQueries({
                  queryKey: CONTENT_PROFILES_QUERY_KEY,
                })
              }
            >
              Refresh
            </DSButton>
          </DSBox>

          {!profiles?.length ? (
            <DSTypography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No content profiles yet.
            </DSTypography>
          ) : (
            <DSTableContainer>
              <DSTable size="small">
                <DSTableHead>
                  <DSTableRow>
                    <DSTableCell>Name</DSTableCell>
                    <DSTableCell>Description</DSTableCell>
                    <DSTableCell>Target</DSTableCell>
                    <DSTableCell>Status</DSTableCell>
                    <DSTableCell>Submitted</DSTableCell>
                    <DSTableCell>Created by</DSTableCell>
                    <DSTableCell>Created</DSTableCell>
                    <DSTableCell align="right">Actions</DSTableCell>
                  </DSTableRow>
                </DSTableHead>
                <DSTableBody>
                  {profiles.map((profile) => {
                    const createdDate = profile.createdAt
                      ? new Date(profile.createdAt)
                      : null;
                    return (
                      <DSTableRow
                        key={profile.id}
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(`/admin/profiles/${profile.id}`)
                        }
                      >
                        <DSTableCell>
                          <DSTypography variant="body2" fontWeight={500}>
                            {profile.name}
                          </DSTypography>
                        </DSTableCell>
                        <DSTableCell sx={{ maxWidth: 240 }}>
                          {profile.description ? (
                            <DSTooltip title={profile.description}>
                              <DSTypography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                              >
                                {profile.description}
                              </DSTypography>
                            </DSTooltip>
                          ) : (
                            <DSTypography variant="body2" color="text.disabled">
                              —
                            </DSTypography>
                          )}
                        </DSTableCell>
                        <DSTableCell>
                          {profile.orderName
                            ? `Order ${profile.orderName}`
                            : `${profile.deviceCount} device${profile.deviceCount === 1 ? "" : "s"}`}
                        </DSTableCell>
                        <DSTableCell>
                          {isReady(profile) ? (
                            <DSChip size="small" color="info" label="Ready" />
                          ) : (
                            <DSTooltip title="Ignored until marked ready: nothing is pushed">
                              <DSChip size="small" label="Draft (ignored)" />
                            </DSTooltip>
                          )}
                        </DSTableCell>
                        <DSTableCell>
                          {profile.submittedAt
                            ? new Date(profile.submittedAt).toLocaleString()
                            : profile.deadline
                              ? `No — due ${new Date(profile.deadline).toLocaleDateString()}`
                              : "No"}
                        </DSTableCell>
                        <DSTableCell>
                          {profile.createdByName ?? "—"}
                        </DSTableCell>
                        <DSTableCell>
                          {createdDate && !Number.isNaN(createdDate.getTime())
                            ? createdDate.toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "numeric",
                                day: "numeric",
                              })
                            : "—"}
                        </DSTableCell>
                        <DSTableCell
                          align="right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DSBox
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "flex-end",
                            }}
                          >
                            <DSButton
                              size="small"
                              variant="outlined"
                              onClick={() => openRename(profile)}
                            >
                              Edit
                            </DSButton>
                            <DSButton
                              size="small"
                              color="error"
                              onClick={() => setDeleting(profile)}
                            >
                              Delete
                            </DSButton>
                          </DSBox>
                        </DSTableCell>
                      </DSTableRow>
                    );
                  })}
                </DSTableBody>
              </DSTable>
            </DSTableContainer>
          )}
        </DSBox>
      )}

      <ContentProfileModal
        open={isModalOpen}
        profile={editing}
        onClose={() => setIsModalOpen(false)}
      />

      <ConfirmationModal
        open={!!deleting}
        message={
          deleting
            ? `Delete "${deleting.name}"? This only removes the profile — it does not change anything on its ${deleting.deviceCount} assigned device${deleting.deviceCount === 1 ? "" : "s"}.`
            : ""
        }
        onClose={() => setDeleting(null)}
        onConfirm={handleConfirmDelete}
      />

      <DSDialog
        open={!!notice}
        onClose={() => setNotice(null)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogContent>
          <DSTypography variant="body2">{notice}</DSTypography>
        </DSDialogContent>
        <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
          <DSButton
            variant="contained"
            size="small"
            onClick={() => setNotice(null)}
          >
            Close
          </DSButton>
        </DSDialogActions>
      </DSDialog>
    </DSBox>
  );
};

export default ContentProfiles;
